import {BASIC_COINS_INCOME} from "../../../configs/income";
import {checkIfHexBlockedByEnemyUnit} from "../checkIfHexBlockedByEnemyUnit";
import {KEY_POINT_TOKENS, SPECIAL_BUILDING_TOKENS, TOKENS} from "../../../constants/tokens";
import {calcTempleIncomeDetails} from "./calcTempleIncome";
import {calcGuildIncomeDetails} from "./calcGuildIncome";
import {calcMineIncomeDetails} from "./calcMineIncome";
import {calcRuinsIncomeDetails} from "./calcRuinsIncome";
import {FACTIONS} from "../../../constants/factions";
import {LEPRECHAUNS_ADDITIONAL_BASIC_INCOME} from "../../../configs/factions/leprechauns";
import {isPlayerFaction} from "../../factions/isPlayerFaction";
import {isPlayerPolicy} from "../../factions/isPlayerPolicy";
import {POLICIES} from "../../../constants/policies";
import {isHegemonismBonusHexKey} from "../../policy/hegemonism/isHegemonismBonusHexKey";
import {POLICY_HEGEMONISM_QUARTER_WIN_POINTS_INCOME_BONUS} from "../../../configs/policy/hegemonism";
import {POLICY_TOTALITARIANISM_MILITARY_BUILDINGS_WIN_POINTS_RATIO} from "../../../configs/policy/totalitarianism";
import {getPlayerBonusValues} from "../getPlayerBonusValues";
import {checkIfPlayerBonusApplies} from "../checkIfPlayerBonusApplies";
import {NEUTRAL_PLAYER_COLOR} from "../../../constants/players";

// Доплаты к доходу — монетами (additions) и очками победы (winPointsAdditions)
export const INCOME_ADDITION_IDS = Object.freeze({
  LEPRECHAUNS: 'leprechauns',
  PLAYER_BONUS: 'player_bonus',
});

// Чем платит источник дохода
export const INCOME_RESOURCES = Object.freeze({
  COINS: 'coins',
  WIN_POINTS: 'winPoints',
});

const MILITARY_BUILDING_TOKENS = [TOKENS.BARRACKS, SPECIAL_BUILDING_TOKENS.ARSENAL, SPECIAL_BUILDING_TOKENS.MILITARY_CAMP];

/**
 * Доход игрока за раунд по источникам — единственный расчет дохода:
 * updatePlayersIncome берет из него итоги (coinsIncome, winPointsIncome), а
 * панель статов в шапке — расшифровку. Заблокированный источник (вражеский
 * отряд рядом, повреждение) остается в списке с amount 0 и isBlocked
 *
 * @param {Object} player
 * @param {Object} params
 * @param {Object} params.citiesCache - { [playerColor]: [city, ...] }
 * @param {Array} [params.units]
 * @param {Object} params.hexes
 * @param {Object} params.specialBuildings
 * @param {number} params.round
 * @param {Object} params.buildingsDamage
 * @param {string} params.gameType - getRulesGameType(gameState)
 * @param {number|null} params.ruinsIncomeRoundLimit - getRuinsIncomeRoundLimit(gameState)
 * @param {boolean} [params.isCitylessEconomyRule] - checkIfCitylessEconomyRule(gameState):
 *   без единого города нет и доплат к базовому доходу; false — только у
 *   записей, сделанных до правила
 * @returns {{
 *   citiesCount: number,
 *   coinsIncome: number,
 *   winPointsIncome: number,
 *   basicCoins: number,
 *   additions: Array<{id: string, amount: number, isPaid: boolean, perCity?: number, fixed?: number}>,
 *   winPointsAdditions: Array<{id: string, amount: number, isPaid: boolean, perCity?: number, fixed?: number}>,
 *   sources: Array<{token: string, hexKey: string, resource: string, isBlocked: boolean, amount: number, details: Object|null}>,
 *   totalitarianism: {militaryBuildingsCount: number, amount: number}|null,
 * }}
 */
export const calcPlayerIncomeBreakdown = (player, {
  citiesCache,
  units = [],
  hexes,
  specialBuildings,
  round,
  buildingsDamage,
  gameType,
  ruinsIncomeRoundLimit,
  isCitylessEconomyRule = true,
}) => {
  const cities = citiesCache[player.color] || [];
  // Нейтральные города дохода с источников не дают (базовый доход считается
  // по наличию городов — как и прежде)
  const sourceCities = player.color === NEUTRAL_PLAYER_COLOR ? [] : cities;
  const enemyCities = Object.entries(citiesCache)
    .filter(([color]) => color !== player.color)
    .flatMap(([, colorCities]) => colorCities);

  const sources = [];
  const isBlocked = (hexKey, token) => checkIfHexBlockedByEnemyUnit(
    hexKey, token, player.id, units, hexes, specialBuildings, buildingsDamage
  );
  const addSource = (token, hexKey, resource, calcDetails) => {
    const blocked = isBlocked(hexKey, token);
    const details = blocked ? null : calcDetails();
    sources.push({token, hexKey, resource, isBlocked: blocked, amount: details ? details.total : 0, details});
  };

  for (const city of sourceCities) {
    for (const mineToken of [KEY_POINT_TOKENS.MINE, KEY_POINT_TOKENS.SUPER_MINE]) {
      for (const hexKey of (city[mineToken] || [])) {
        addSource(mineToken, hexKey, INCOME_RESOURCES.COINS, () => calcMineIncomeDetails(
          hexKey, mineToken, player, citiesCache, units, hexes, specialBuildings, buildingsDamage
        ));
      }
    }

    for (const ruinsToken of [KEY_POINT_TOKENS.RUINS, KEY_POINT_TOKENS.SUPER_RUINS]) {
      for (const hexKey of (city[ruinsToken] || [])) {
        addSource(ruinsToken, hexKey, INCOME_RESOURCES.WIN_POINTS, () => calcRuinsIncomeDetails(
          ruinsToken, player, round, ruinsIncomeRoundLimit
        ));
      }
    }

    for (const hexKey of (city[SPECIAL_BUILDING_TOKENS.GUILD] || [])) {
      addSource(SPECIAL_BUILDING_TOKENS.GUILD, hexKey, INCOME_RESOURCES.COINS, () => calcGuildIncomeDetails(
        hexKey, player, enemyCities, hexes
      ));
    }

    for (const hexKey of (city[SPECIAL_BUILDING_TOKENS.TEMPLE] || [])) {
      addSource(SPECIAL_BUILDING_TOKENS.TEMPLE, hexKey, INCOME_RESOURCES.WIN_POINTS, () => calcTempleIncomeDetails(
        hexKey, player, enemyCities, hexes
      ));
    }

    // Гегемонизм: кварталы на бонусных ячейках приносят очки
    if (isPlayerPolicy(player, POLICIES.HEGEMONISM)) {
      for (const hexKey of [...(city[TOKENS.QUARTER] || []), city.centralQuarter]) {
        if (isHegemonismBonusHexKey(hexKey)) {
          addSource(TOKENS.QUARTER, hexKey, INCOME_RESOURCES.WIN_POINTS, () => ({
            hegemonism: POLICY_HEGEMONISM_QUARTER_WIN_POINTS_INCOME_BONUS,
            total: POLICY_HEGEMONISM_QUARTER_WIN_POINTS_INCOME_BONUS,
          }));
        }
      }
    }
  }

  // Тоталитаризм: очки за каждые N рабочих военных строений
  let totalitarianism = null;
  if (isPlayerPolicy(player, POLICIES.TOTALITARIANISM)) {
    let militaryBuildingsCount = 0;
    for (const city of sourceCities) {
      for (const token of MILITARY_BUILDING_TOKENS) {
        militaryBuildingsCount += (city[token] || []).filter((hexKey) => !isBlocked(hexKey, token)).length;
      }
    }
    totalitarianism = {
      militaryBuildingsCount,
      amount: Math.floor(militaryBuildingsCount / POLICY_TOTALITARIANISM_MILITARY_BUILDINGS_WIN_POINTS_RATIO),
    };
  }

  // Базовый доход — только пока есть хотя бы один город. Доплата лепреконам —
  // на тех же условиях; в старых записях — как тогда, без условия (см.
  // checkIfCitylessEconomyRule)
  const basicCoins = cities.length > 0 ? BASIC_COINS_INCOME : 0;
  const isAdditionPaid = cities.length > 0 || !isCitylessEconomyRule;
  const additions = [];
  if (isPlayerFaction(player, FACTIONS.LEPRECHAUNS)) {
    additions.push({
      id: INCOME_ADDITION_IDS.LEPRECHAUNS,
      amount: isAdditionPaid ? LEPRECHAUNS_ADDITIONAL_BASIC_INCOME : 0,
      isPaid: isAdditionPaid,
    });
  }
  // Бонус ботов/лобби (см. configs/playerBonus): монеты к доходу, пока есть
  // хотя бы один город, и за КАЖДЫЙ город игрока — заблокированы ли его
  // кварталы, неважно; у «божества» так же очки победы. Без городов 0. Очки —
  // отдельным списком: доплаты выше платятся монетами
  const winPointsAdditions = [];
  if (checkIfPlayerBonusApplies(player, gameType)) {
    const bonusValues = getPlayerBonusValues(player);
    const pushBonusAddition = (list, fixedAmount = 0, perCity = 0) => {
      if (fixedAmount <= 0 && perCity <= 0) {
        return;
      }
      // Фиксированная доплата платится, как базовый доход, пока есть хотя бы
      // один город (в записях до правила городов — и без них)
      const fixedPaid = fixedAmount > 0 && isAdditionPaid;
      const amount = (fixedPaid ? fixedAmount : 0) + perCity * cities.length;
      list.push({
        id: INCOME_ADDITION_IDS.PLAYER_BONUS,
        amount,
        isPaid: fixedPaid || (perCity > 0 && cities.length > 0),
        ...(perCity > 0 ? {perCity} : {}),
        // Фиксированная часть рядом со ставкой за город — для подписи суммы
        ...(perCity > 0 && fixedPaid ? {fixed: fixedAmount} : {}),
      });
    };
    pushBonusAddition(additions, bonusValues.coinsIncomeBonus, bonusValues.coinsIncomePerCityBonus);
    pushBonusAddition(winPointsAdditions, bonusValues.winPointsIncomeBonus, bonusValues.winPointsIncomePerCityBonus);
  }

  const sumSources = (resource) => sources
    .filter((source) => source.resource === resource)
    .reduce((sum, source) => sum + source.amount, 0);

  return {
    citiesCount: cities.length,
    coinsIncome: basicCoins
      + additions.reduce((sum, addition) => sum + addition.amount, 0)
      + sumSources(INCOME_RESOURCES.COINS),
    winPointsIncome: sumSources(INCOME_RESOURCES.WIN_POINTS)
      + (totalitarianism ? totalitarianism.amount : 0)
      + winPointsAdditions.reduce((sum, addition) => sum + addition.amount, 0),
    basicCoins,
    additions,
    winPointsAdditions,
    sources,
    totalitarianism,
  };
};
